<script lang="ts" setup>
  import { computed, ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import PencilIcon from '@/shared/components/icons/PencilIcon.vue'
  import TrashIcon from '@/shared/components/icons/TrashIcon.vue'
  import DeleteModal from './components/DeleteModal/DeleteModal.vue'
  import type { TravelerInformation, TravelerFieldData } from './TravelerCard.types'
  import {
    HOLDER_FIELDS_INFORMATION,
    MEMBER_FIELDS_INFORMATION,
    MEMBER_FIELDS,
    HOLDER_FIELDS,
  } from './TravelerCard.constants'
  import type { Locale } from '@/shared/types/common.types'

  interface Props {
    isHolder?: boolean
    name: string
    information: TravelerInformation
  }

  interface Emits {
    (e: 'edit'): void
    (e: 'delete'): void
  }

  const props = withDefaults(defineProps<Props>(), {
    isHolder: false,
  })

  const emit = defineEmits<Emits>()

  const { locale, t } = useI18n()

  const isDeleteModalActive = ref(false)
  const onDelete = (status: boolean) => {
    if (status) emit('delete')
    isDeleteModalActive.value = false
  }
  const openDeleteModal = () => {
    isDeleteModalActive.value = true
  }
  const onEdit = () => emit('edit')

  const informationFields = computed((): { [key: string]: TravelerFieldData } => {
    return props.isHolder ? HOLDER_FIELDS_INFORMATION : MEMBER_FIELDS_INFORMATION
  })

  const informationFieldsOrder = computed((): (keyof TravelerInformation)[] => {
    const order = props.isHolder ? HOLDER_FIELDS : MEMBER_FIELDS
    // TODO: add type for field
    // @ts-ignore
    return order.filter((field) => props.information[field] !== undefined)
  })

  const formatField = (field: keyof TravelerInformation) => {
    const format = informationFields.value[field].format
    const informationField = props.information[field]
    if (format) {
      return format(informationField, locale.value as Locale, t)
    }
    return informationField
  }
</script>

<template>
  <div class="traveler-card">
    <template v-if="!isDeleteModalActive">
      <div class="traveler-card__title">
        <p class="traveler-card__title__name">{{ name }}</p>
        <div class="traveler-card__icons">
          <button v-if="!isHolder" data-testid="traveler-card-remove" @click="openDeleteModal">
            <TrashIcon class="traveler-card__icon" />
          </button>

          <button data-testid="traveler-card-edit" @click="onEdit">
            <PencilIcon variant="dark" class="traveler-card__icon" />
          </button>
        </div>
      </div>

      <!-- <div v-if="information.withWinterSport" class="traveler-card__badge-container">
        <div class="traveler-card__badge">
          {{ $t('outbound_sport_cover_winter_name') }}
        </div>
      </div> -->

      <div class="traveler-card__information-rows">
        <div
          v-for="field in informationFieldsOrder"
          :key="field"
          class="traveler-card__information-row"
        >
          <span v-if="informationFields[field].translation">
            {{ $t(informationFields[field].translation) + ': ' }}
          </span>
          {{ formatField(field) }}
        </div>
      </div>
    </template>

    <DeleteModal v-else @delete="onDelete" />
  </div>
</template>

<style lang="scss" scoped>
  @import './TravelerCard';
</style>
